/**
 * Desktop sidebar — renders the 5-trunk site tree from lib/navTree.js
 * (My Project · Buyers' Group · Rights & Remedies · Buying Smart · Learn) + My Account, For Developers, Admin.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Home, Building2, User, Users, Briefcase, 
  Moon, Sun, ChevronRight, ChevronDown, LogOut, Lock, 
  Newspaper, BookOpen, Sparkles,
  ClipboardCheck, Scale, Globe, Shield, Crown, FileText,
  TrendingUp, Phone, MapPin, FileDown,
  MessageCircle, Banknote, FileCheck, Calculator,
  Heart, Plus, Wrench, ShoppingCart, Target, Search,
  AlertCircle, CheckCircle, Building, Landmark, FileSearch,
  Mail, Bell, Calendar, Gavel, AlertTriangle, Info,
  ShieldCheck, Package, Key,
  Eye,
} from 'lucide-react';
import axios from 'axios';
import { NAV_TREE, trunkOf, visibleLimbs, DEFAULT_PROJECT_ID, trunkLabel } from '@/lib/navTree';
import useGroupMenu, { goToGroupStage } from '@/components/group/useGroupMenu';

const API = process.env.REACT_APP_BACKEND_URL;

// Color themes for each facet
const FACET_COLORS = {
  primary: {
    bg: 'bg-primary/5', border: 'border-primary/20', text: 'text-primary', icon: 'text-primary', badge: 'bg-primary/10 text-primary'
  },
  slate: {
    bg: 'bg-slate-50 dark:bg-slate-900/30', border: 'border-slate-200 dark:border-slate-700', text: 'text-slate-700 dark:text-slate-300',
    icon: 'text-slate-600 dark:text-slate-400', badge: 'bg-slate-100 text-slate-700'
  },
  recourse: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-300',
    icon: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
  },
  risk: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
  },
  community: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300',
    icon: 'text-green-600 dark:text-green-400',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
  },
  services: {
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    border: 'border-teal-200 dark:border-teal-800',
    text: 'text-teal-700 dark:text-teal-300',
    icon: 'text-teal-600 dark:text-teal-400',
    badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300'
  },
  developer: {
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    border: 'border-indigo-200 dark:border-indigo-800',
    text: 'text-indigo-700 dark:text-indigo-300',
    icon: 'text-indigo-600 dark:text-indigo-400',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
  }
};

const TREE_COLOR = { primary: 'primary', green: 'community', amber: 'recourse', blue: 'risk', slate: 'slate' };

// Section IDs
const SECTIONS = {
  EXPLORE: 'explore',
  RECOURSE: 'recourse',
  RISK: 'risk',
  COMMUNITY: 'community',
  SERVICES: 'services',
  DEVELOPER: 'developer',
  ACCOUNT: 'account',
  CONTENT: 'content',
  ADMIN: 'admin'
};

export default function Sidebar({ 
  activeSection, 
  onSectionChange, 
  sidebarCollapsed, 
  onOpenAuth,
  user,
  selectedProjectId,
  onScrollToTop,
  darkMode,
  onToggleDarkMode,
  onLogout
}) {
  const [featureVisibility, setFeatureVisibility] = useState({
    legal_services: true,
    rera_public_record: true,
    fbc_curated_info: true,
    browse_projects: true,
    service_providers: true,
    home_services: true
  });
  
  // Progressive disclosure tier states
  const [tierStatus, setTierStatus] = useState({
    services: { enabled: true },
    classifieds: { enabled: true },
    developer_updates: { enabled: true },
    referrals: { enabled: true },
    recourse: { enabled: true },
    checklist: { enabled: true },
    community: { enabled: true },
    explore: { enabled: true }
  });
  
  const [expandedSection, setExpandedSection] = useState(null);

  // Lifecycle-aware nav: post-OC projects surface living concerns first
  const [isPostOc, setIsPostOc] = useState(false);
  useEffect(() => {
    const pid = selectedProjectId || user?.project_id || DEFAULT_PROJECT_ID;
    axios.get(`${API}/api/projects/${pid}/lifecycle`)
      .then((r) => setIsPostOc(r.data?.stage === 'post_oc'))
      .catch(() => setIsPostOc(false));
  }, [selectedProjectId, user?.project_id]);
  
  const canAccessLMS = user?.role === 'admin';
  const isVerifiedBuyer = user?.role === 'admin' ||
                          user?.verification_status === 'verified' ||
                          user?.verified_projects?.length > 0 || 
                          user?.project_verifications?.some(v => v.status === 'verified');
  const groupMenu = useGroupMenu(selectedProjectId || user?.project_id || DEFAULT_PROJECT_ID, !!isVerifiedBuyer);

  useEffect(() => {
    fetchFeatureVisibility();
    fetchTierStatus();
  }, []);

  // Auto-expand the trunk that holds the active section
  useEffect(() => {
    const t = trunkOf(activeSection);
    if (t) setExpandedSection(t);
    else if (['me', 'my-documents', 'verify-ownership', 'subscription'].includes(activeSection)) setExpandedSection(SECTIONS.ACCOUNT);
    else if (activeSection?.startsWith('developer') || activeSection?.startsWith('dev-')) setExpandedSection(SECTIONS.DEVELOPER);
    else if (activeSection === 'admin') setExpandedSection(SECTIONS.ADMIN);
  }, [activeSection]);

  const fetchFeatureVisibility = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/feature-visibility`);
      setFeatureVisibility(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.log('Using default feature visibility');
    }
  };

  const fetchTierStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/tier-status`);
      if (res.data?.tiers) {
        setTierStatus(prev => ({ ...prev, ...res.data.tiers }));
      }
    } catch (err) {
      console.log('Using default tier status');
    }
  };

  // Helper to check if a tier feature is enabled (admin always sees everything)
  const isTierEnabled = (featureKey) => canAccessLMS || tierStatus[featureKey]?.enabled !== false;
  const getTierTeaser = (featureKey) => tierStatus[featureKey]?.teaser || 'Coming soon';

  const toggleSection = (sectionId) => {
    setExpandedSection(prev => prev === sectionId ? null : sectionId);
  };

  const handleNavClick = (section) => {
    onSectionChange(section);
  };

  return (
    <aside 
      className={`fixed left-0 top-0 h-[calc(100%-60px)] z-40 bg-background/95 backdrop-blur-md border-r transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} hidden lg:flex flex-col`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-3 cursor-pointer" onClick={onScrollToTop}>
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center shrink-0">
            <span className="text-primary-foreground font-bold text-sm">FBC</span>
          </div>
          {!sidebarCollapsed && (
            <div>
              <div className="font-semibold text-sm whitespace-nowrap" style={{ fontFamily: 'Playfair Display, serif' }}>Flat Buyers' Connect</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Navigation */}
      <ScrollArea className="flex-1 py-2 overflow-x-hidden [&>[data-radix-scroll-area-viewport]>div]:!block">
        <div className="px-2 space-y-1">
          
          {/* HOME - Always visible */}
          <NavButton
            icon={Home}
            label="Home"
            active={activeSection === 'home'}
            collapsed={sidebarCollapsed}
            onClick={() => { onScrollToTop(); setExpandedSection(null); }}
            testId="sidebar-home"
          />

          {/* THE FIVE TRUNKS — rendered from lib/navTree.js (single source of truth) */}
          {NAV_TREE.map((trunk) => {
            const color = FACET_COLORS[TREE_COLOR[trunk.color]];
            const locked = trunk.gated && !isVerifiedBuyer;
            const limbs = visibleLimbs(trunk, { isPostOc });
            return (
              <div key={trunk.id} className="pt-2" data-testid={`nav-trunk-${trunk.id}`}>
                <FacetHeader
                  id={trunk.id} icon={trunk.icon} label={trunkLabel(trunk, user)} subtitle={trunk.subtitle}
                  color={color} collapsed={sidebarCollapsed}
                  isExpanded={expandedSection === trunk.id}
                  onToggle={() => toggleSection(trunk.id)}
                  onClick={() => { handleNavClick(trunk.landing); setExpandedSection(trunk.id); }}
                  active={activeSection === trunk.landing} locked={locked}
                />
                {expandedSection === trunk.id && !sidebarCollapsed && (
                  <div className={`ml-4 pl-2 border-l-2 ${color.border} space-y-0.5 mt-1 min-w-0 overflow-hidden`}>
                    {locked ? (
                      <>
                        <NavItem icon={Eye} label="See what members get" active={activeSection === trunk.landing}
                          onClick={() => handleNavClick(trunk.landing)} testId={`nav-preview-${trunk.id}`} />
                        <NavItem icon={Lock} label={user ? 'Verify ownership to join' : 'Sign in to join'} active={false}
                          onClick={() => user ? handleNavClick('verify-ownership') : onOpenAuth?.('login')} />
                      </>
                    ) : trunk.id === 'group' ? groupMenu.map((e) => (
                      e.type === 'heading' ? (
                        <button key={`h-${e.key}`} type="button" onClick={() => goToGroupStage(handleNavClick, e.key)} title={e.hint}
                          className="w-full text-left px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                          data-testid={`nav-stage-${e.key}`}>{e.label}</button>
                      ) : e.type === 'divider' ? (
                        <div key={`d-${e.key}`} className="border-t my-1.5" />
                      ) : e.type === 'empty' ? (
                        <p key={`e-${e.key}`} className="px-2 text-[11px] text-muted-foreground italic" data-testid={`nav-stage-empty-${e.key}`}>No open matters</p>
                      ) : (
                        <NavItem key={e.id} icon={e.icon} label={e.label} active={activeSection === e.id}
                          onClick={() => handleNavClick(e.id)} testId={`nav-limb-${e.id}`} badge={e.badge} />
                      )
                    )) : limbs.map((l) => (
                      <NavItem key={l.id} icon={l.icon} label={l.label} active={activeSection === l.id}
                        onClick={() => handleNavClick(l.id)} testId={`nav-limb-${l.id}`}
                        badge={undefined} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* ADMIN */}
          {user?.role === 'admin' && (
            <>
              <Separator className="my-3" />
              <AccordionGroup
                id={SECTIONS.ADMIN} icon={Crown} label="Admin Panel" collapsed={sidebarCollapsed}
                isExpanded={expandedSection === SECTIONS.ADMIN} onToggle={() => toggleSection(SECTIONS.ADMIN)}
                itemCount={1} badge="Admin"
              >
                <NavItem icon={Crown} label="Admin Dashboard" active={activeSection === 'admin'} onClick={() => handleNavClick('admin')} />
              </AccordionGroup>
            </>
          )}

          {/* ─────────────────────────────────────────────── */}
          {/* UTILITY: My Account */}
          {/* ─────────────────────────────────────────────── */}
          {user && (
            <>
              <Separator className="my-3" />
              <AccordionGroup
                id={SECTIONS.ACCOUNT}
                icon={User}
                label="My Account"
                collapsed={sidebarCollapsed}
                isExpanded={expandedSection === SECTIONS.ACCOUNT}
                onToggle={() => toggleSection(SECTIONS.ACCOUNT)}
                itemCount={isVerifiedBuyer ? 3 : 4}
              >
                <NavItem icon={User} label="Profile & Details" active={activeSection === 'me'} 
                  onClick={() => handleNavClick('me')} />
                <NavItem icon={FileText} label="Ownership Documents" active={activeSection === 'my-documents'} 
                  onClick={() => handleNavClick('my-documents')} />
                <NavItem icon={FileText} label="My project requests" active={activeSection === 'my-submissions'}
                  onClick={() => handleNavClick('my-submissions')} testId="nav-my-submissions" />
                <NavItem icon={Crown} label="Membership" active={activeSection === 'subscription'}
                  onClick={() => handleNavClick('subscription')} testId="nav-subscription" />
                {!isVerifiedBuyer && (
                  <NavItem icon={CheckCircle} label="Verify Ownership" active={activeSection === 'verify-ownership'} 
                    onClick={() => handleNavClick('verify-ownership')} badge="Required" badgeVariant="outline" />
                )}
              </AccordionGroup>
            </>
          )}

          {/* FOR DEVELOPERS — signed-in only; visitors find it in the footer */}
          {user && <AccordionGroup
            id={SECTIONS.DEVELOPER} icon={Building} label="For Developers" collapsed={sidebarCollapsed}
            isExpanded={expandedSection === SECTIONS.DEVELOPER} onToggle={() => toggleSection(SECTIONS.DEVELOPER)}
            itemCount={user?.role === 'developer' ? 3 : 1}
          >
            {user?.role === 'developer' ? (
              <>
                <NavItem icon={TrendingUp} label="Developer Console" active={activeSection === 'developer-console'} onClick={() => handleNavClick('developer-console')} />
                <NavItem icon={CheckCircle} label="Buyer Verifications" active={activeSection === 'dev-verifications'} onClick={() => handleNavClick('dev-verifications')} />
                <NavItem icon={Newspaper} label="Post Updates" active={activeSection === 'dev-post-updates'} onClick={() => handleNavClick('dev-post-updates')} />
              </>
            ) : (
              <NavItem icon={Plus} label="Register as Developer" active={activeSection === 'developer-register'} onClick={() => handleNavClick('developer-register')} />
            )}
          </AccordionGroup>}

        </div>
      </ScrollArea>
      
      {/* Footer */}
      <div className="p-3 border-t space-y-2">
        <Button
          variant="ghost"
          className={`w-full ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start'}`}
          onClick={onToggleDarkMode}
        >
          {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {!sidebarCollapsed && <span className="ml-2 text-sm">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>}
        </Button>
        
        {/* Join Legal Panel Link - visible when not logged in */}
        {!user && !sidebarCollapsed && (
          <Button
            variant="ghost"
            className="w-full justify-start text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
            onClick={() => onSectionChange('lawyer-registration')}
          >
            <Scale className="h-4 w-4" />
            <span className="ml-2 text-sm">Join as RERA Lawyer</span>
          </Button>
        )}
        
        {/* Lawyer Dashboard - visible for lawyers */}
        {user?.role === 'lawyer' && (
          <Button
            variant={activeSection === 'lawyer-dashboard' ? "secondary" : "ghost"}
            className={`w-full ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start'} ${
              activeSection === 'lawyer-dashboard' ? 'bg-indigo-100 text-indigo-700' : 'text-indigo-600'
            }`}
            onClick={() => onSectionChange('lawyer-dashboard')}
          >
            <Scale className="h-4 w-4" />
            {!sidebarCollapsed && <span className="ml-2 text-sm">My Legal Dashboard</span>}
          </Button>
        )}
        
        {user ? (
          <Button
            variant="ghost"
            className={`w-full text-destructive hover:text-destructive hover:bg-destructive/10 ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start'}`}
            onClick={onLogout}
            data-testid="sidebar-logout"
          >
            <LogOut className="h-4 w-4" />
            {!sidebarCollapsed && <span className="ml-2 text-sm">Logout</span>}
          </Button>
        ) : (
          <Button
            variant="default"
            className={`w-full ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start'}`}
            onClick={() => onOpenAuth('login')}
            data-testid="sidebar-sign-in"
          >
            <User className="h-4 w-4" />
            {!sidebarCollapsed && <span className="ml-2 text-sm">Sign In</span>}
          </Button>
        )}
      </div>
    </aside>
  );
}

// Zone Divider - Visual separator for facets
function ZoneDivider({ label, color, collapsed, dimmed }) {
  if (collapsed) return <Separator className="my-2" />;
  
  return (
    <div className="pt-4 pb-1 px-1">
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${dimmed ? 'text-muted-foreground/50' : color.text}`}>
        {label}
      </div>
    </div>
  );
}

// Facet Header - Clickable zone header that navigates to landing page
function FacetHeader({ id, icon: Icon, label, subtitle, color, collapsed, isExpanded, onToggle, onClick, active, locked, dimmed }) {
  if (collapsed) {
    return (
      <Button
        variant="ghost"
        className={`w-full justify-center px-2 ${dimmed ? 'opacity-40' : color.bg}`}
        onClick={dimmed ? undefined : onClick}
        title={label}
        disabled={dimmed}
      >
        <Icon className={`h-4 w-4 ${dimmed ? 'text-muted-foreground' : color.icon}`} />
      </Button>
    );
  }
  
  return (
    <div className={`rounded-lg ${dimmed ? 'bg-muted/30 border-muted opacity-60' : `${color.bg} ${color.border}`} border`}>
      <div
        className={`w-full flex items-center justify-between h-auto py-2 px-3 ${dimmed ? 'cursor-default' : 'cursor-pointer hover:opacity-80'} transition-opacity`}
        data-testid={`sidebar-facet-${id}`}
      >
        <div className="flex items-center gap-2 flex-1" onClick={dimmed ? undefined : onClick}>
          <Icon className={`h-4 w-4 ${dimmed ? 'text-muted-foreground' : color.icon}`} />
          <div className="text-left">
            <div className={`text-sm font-medium ${dimmed ? 'text-muted-foreground' : color.text}`}>{label}</div>
            <div className={`text-[10px] ${dimmed ? 'text-muted-foreground/70 italic' : 'text-muted-foreground'}`}>{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(locked || dimmed) && <Lock className="h-3 w-3 text-muted-foreground" />}
          {!dimmed && (
            <button
              type="button"
              className="h-6 w-6 p-0 flex items-center justify-center rounded hover:bg-black/5 dark:hover:bg-white/5"
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
            >
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Accordion Group Component (for utility sections)
function AccordionGroup({ id, icon: Icon, label, collapsed, isExpanded, onToggle, children, itemCount, badge, badgeVariant = "secondary" }) {
  if (collapsed) {
    return (
      <Button
        variant="ghost"
        className="w-full justify-center px-2"
        onClick={onToggle}
        title={label}
      >
        <Icon className="h-4 w-4" />
      </Button>
    );
  }
  
  return (
    <div className="space-y-0.5">
      <Button
        variant="ghost"
        className="w-full justify-between h-9 px-3"
        onClick={onToggle}
        data-testid={`sidebar-${id}`}
      >
        <div className="flex items-center">
          <Icon className="h-4 w-4 shrink-0" />
          <span className="ml-2 text-sm truncate">{label}</span>
          {badge && (
            <Badge 
              variant={badgeVariant} 
              className={`ml-2 text-[9px] px-1.5 py-0 ${badgeVariant === 'outline' ? 'border-amber-400 text-amber-600 bg-amber-50' : ''}`}
            >
              {badge}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground">{itemCount}</span>
          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </div>
      </Button>
      
      {isExpanded && (
        <div className="ml-4 pl-2 border-l border-border/50 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

// Simple Nav Button (for Home)
function NavButton({ icon: Icon, label, active, collapsed, onClick, testId }) {
  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      className={`w-full ${collapsed ? 'justify-center px-2' : 'justify-start'} ${active ? 'bg-primary/10 text-primary' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <Icon className="h-4 w-4" />
      {!collapsed && <span className="ml-2 text-sm">{label}</span>}
    </Button>
  );
}

// Nav Item inside Accordion/Facet
function NavItem({ icon: Icon, label, active, onClick, badge, badgeVariant = "secondary", locked, colorTheme, testId }) {
  const themeColors = {
    amber: 'hover:bg-amber-100/50 dark:hover:bg-amber-900/30',
    blue: 'hover:bg-blue-100/50 dark:hover:bg-blue-900/30',
    green: 'hover:bg-green-100/50 dark:hover:bg-green-900/30',
    teal: 'hover:bg-teal-100/50 dark:hover:bg-teal-900/30',
    indigo: 'hover:bg-indigo-100/50 dark:hover:bg-indigo-900/30'
  };
  
  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      size="sm"
      className={`w-full min-w-0 overflow-hidden justify-start h-8 ${active ? 'bg-primary/10 text-primary' : ''} ${colorTheme ? themeColors[colorTheme] : ''}`}
      onClick={onClick}
      data-testid={testId}
      title={label}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="ml-2 text-xs truncate min-w-0">{label}</span>
      {locked && <Lock className="h-3 w-3 ml-auto text-muted-foreground" />}
      {badge && !locked && (
        <Badge 
          variant={badgeVariant} 
          className={`ml-auto shrink-0 text-[9px] px-1 py-0 ${badgeVariant === 'outline' ? 'border-muted-foreground/30' : ''}`}
        >
          {badge}
        </Badge>
      )}
    </Button>
  );
}
